import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, AlertCircle, Edit2, Plus, X, CheckCircle2, ArrowRight } from 'lucide-react';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';

/**
 * Inline card showing the currently-selected Roof Screenshot for a
 * property. Iter179 — renamed from "Roof Diagram" (which now refers to
 * the upcoming vector templates) to "Roof Screenshot" (Google-Earth
 * style photo underlay).
 *
 * Iter180 — adds:
 *   • Green "Saved" pulse after a successful screenshot persist so
 *     the operator visually sees the save land.
 *   • "Next: Choose Roof Template →" primary CTA that jumps to the
 *     Roof Templates library with a context banner showing this
 *     property + screenshot pinned at the top.
 */
export default function RoofDiagramPickerInline({ api, appraisalId, propertyId, onChanged, compact = false }) {
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState(null);
  // Iter180 Phase 3 — the accepted Main Roof Template (if any) so we
  // can show its thumbnail under the screenshot card and link back.
  const [acceptedTemplate, setAcceptedTemplate] = useState(null);
  // Iter180 — short-lived "Saved" flash after a successful persist.
  const [justSaved, setJustSaved] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
      // Read both field names so legacy data and new data both work.
      const pid = data?.roof_screenshot_photo_id || data?.roof_diagram_photo_id;
      if (pid) {
        try {
          const { data: photos } = await api.get(`/photos/by-property/${propertyId}`);
          const match = (photos || []).find(p => p._id === pid);
          setPhoto(match || null);
        } catch { setPhoto(null); }
      } else {
        setPhoto(null);
      }
      // Iter180 Phase 3 — fetch the accepted template metadata so the
      // card can show its name + thumb.
      const tplId = data?.roof_template_id;
      if (tplId) {
        try {
          const { data: tpl } = await api.get(`/roof-templates/${tplId}`);
          setAcceptedTemplate(tpl || null);
        } catch { setAcceptedTemplate(null); }
      } else {
        setAcceptedTemplate(null);
      }
    } catch { /* ignore */ }
  }, [api, propertyId]);

  useEffect(() => { load(); }, [load]);

  const persist = async (photoId) => {
    try {
      // Iter179 — write the new field name. Backend syncs the legacy
      // `roof_diagram_photo_id` alias automatically (read fallback).
      await api.patch(`/properties/${propertyId}`, { roof_screenshot_photo_id: photoId });
      await load();
      onChanged?.();
      // Iter180 — show "Saved" pulse only when a screenshot was added
      // or swapped (not when cleared).
      if (photoId) {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 4000);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save roof screenshot');
    }
  };

  if (!propertyId) return null;
  const hasScreenshot = !!photo;

  // Compose a human address for the Google Earth deep link inside the
  // picker. Falls back to coordinates / suburb where the street is
  // missing.
  const address = prop ? [
    prop.property_address_num,
    prop.property_address_street,
    prop.property_address_suburb,
    prop.property_address_city,
  ].filter(Boolean).join(' ') : '';

  // Iter180 — jump-to-templates URL. The Roof Templates page reads
  // these query params and shows a context banner with the screenshot
  // pinned so the operator can compare each template side-by-side.
  //
  // iter303ai-fix12 · When this picker is mounted inside the Workflow
  // Runner (URL starts with '/workflow'), tack on `return_to=<runner url>`
  // so the templates page's Accept + Back both navigate back to the
  // runner instead of dumping the operator on the Property page.
  const gotoTemplates = () => {
    const qs = new URLSearchParams({
      property_id: propertyId,
      screenshot_photo_id: photo?._id || '',
      address,
    });
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/workflow')) {
      qs.set('return_to', window.location.pathname + window.location.search);
    }
    navigate(`/roof-templates?${qs.toString()}`);
  };

  return (
    <div data-testid="roof-diagram-inline" className={`bg-white border border-[#E2E2DF] rounded-xl ${compact ? 'p-3' : 'p-5'}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-slate-800 flex items-center gap-2 uppercase tracking-[0.14em]`}>
          <ImageIcon size={14} className="text-[#D35400]" /> Roof Screenshot
        </h3>
        <button
          data-testid="roof-diagram-open-picker"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1 text-xs font-semibold text-[#D35400] hover:text-[#A04000]"
        >
          {hasScreenshot ? (<><Edit2 size={11} /> Change</>) : (<><Plus size={11} /> Add Roof Screenshot</>)}
        </button>
      </div>

      {/* Iter180 — green "Saved" flash after a successful save. */}
      {justSaved && (
        <div data-testid="roof-screenshot-saved-flash" className="mb-3 flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-md px-3 py-2 text-sm text-emerald-800 animate-in fade-in slide-in-from-top-1 duration-300">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <span><b>Saved!</b> Roof Screenshot stored for this property. Ready to pick a matching template.</span>
        </div>
      )}

      {hasScreenshot ? (
        <div className="space-y-3">
          {/* Iter180 Phase 3+ — side-by-side layout when an accepted
              template exists, so the operator can see the screenshot
              and the canonical template at a glance. Falls back to a
              single-column when only the screenshot exists. */}
          <div className={acceptedTemplate ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : ''}>
            {/* LEFT — Roof Screenshot */}
            <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-2">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Roof Screenshot</p>
              <div className="flex items-start gap-2">
                <img src={photo.url} alt="roof" className="w-28 h-20 object-cover rounded border border-[#E2E2DF] flex-shrink-0" />
                <div className="flex-1 min-w-0 text-xs">
                  <p className="font-semibold text-slate-800 truncate">{photo.description || photo.original_name || 'Screenshot'}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">Used as the roof underlay on Work Sheet PDFs and as the trace base in Roof Design Templates.</p>
                  <button
                    data-testid="roof-diagram-remove"
                    onClick={() => persist('')}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] text-red-600 hover:text-red-700"
                  ><X size={10} /> Remove</button>
                </div>
              </div>
            </div>
            {/* RIGHT — Main Roof Template (when accepted). Lives on
                the SAME ROW as the screenshot so the operator can
                eyeball the match. When no template yet → the column
                shows a subtle prompt rather than an empty gap. */}
            {acceptedTemplate ? (
              <div data-testid="accepted-roof-template" className="bg-emerald-50 border border-emerald-200 rounded-md p-2">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">Main Roof Template</p>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="edit-accepted-template"
                      onClick={gotoTemplates}
                      className="text-[10px] text-emerald-700 hover:text-emerald-800 font-semibold underline-offset-2 hover:underline"
                      title="Edit or change this template — opens the Roof Templates page with this one pre-selected"
                    >Edit / Change</button>
                    <button
                      data-testid="clear-accepted-template"
                      onClick={async () => {
                        try { await api.patch(`/properties/${propertyId}`, { roof_template_id: '' }); await load(); } catch { /* ignore */ }
                      }}
                      className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold"
                    >Clear</button>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  {/* Iter180+ — thumbnail itself is clickable so the
                      operator can edit/change without hunting for the
                      affordance. */}
                  <button
                    type="button"
                    data-testid="accepted-template-thumb"
                    onClick={gotoTemplates}
                    title="Click to edit or change this template"
                    className="flex-shrink-0 group relative"
                  >
                    <img
                      src={acceptedTemplate.thumbnail_url || `/api/roof-templates/${acceptedTemplate._id}/image`}
                      alt={acceptedTemplate.name}
                      className="w-28 h-20 object-contain bg-white rounded border border-emerald-200 transition group-hover:border-[#D35400] group-hover:shadow-md"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-[#D35400]/85 opacity-0 group-hover:opacity-100 transition rounded">
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white flex items-center gap-1">
                        <Edit2 size={11} /> Edit / Change
                      </span>
                    </div>
                  </button>
                  <div className="flex-1 min-w-0 text-xs">
                    <p className="font-semibold text-slate-800 truncate" title={acceptedTemplate.name}>{acceptedTemplate.name}</p>
                    <div className="flex items-center gap-1 mt-1 text-[10px] flex-wrap">
                      {acceptedTemplate.hip_count > 0    && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">{acceptedTemplate.hip_count} Hip</span>}
                      {acceptedTemplate.ridge_count > 0  && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded font-semibold">{acceptedTemplate.ridge_count} Ridge</span>}
                      {acceptedTemplate.valley_count > 0 && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded font-semibold">{acceptedTemplate.valley_count} Valley</span>}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1.5">Used on Work Sheet PDFs. Next: add pin data to build the Master Annotated Diagram.</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Primary CTA — handoff to template flow. */}
          <button
            data-testid="goto-roof-templates-btn"
            onClick={gotoTemplates}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-[#4A6FA5] hover:bg-[#3a5a89] text-white text-sm font-semibold rounded-md shadow-sm hover:shadow-md transition"
          >
            <span className="flex items-center gap-2">
              <ImageIcon size={14} /> {acceptedTemplate ? 'Change Roof Template' : 'Next: Choose Roof Template'}
            </span>
            <ArrowRight size={14} />
          </button>
          {!acceptedTemplate && <p className="text-[11px] text-slate-400 -mt-1">Or trace a new template from this screenshot on the templates page.</p>}
        </div>
      ) : (
        <div data-testid="roof-diagram-prompt" className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            No roof screenshot saved for this property yet.&nbsp;
            <button
              onClick={() => setOpen(true)}
              data-testid="roof-diagram-prompt-cta"
              className="font-semibold underline hover:text-amber-900"
            >Upload one now</button>
            &nbsp;(or grab from Google Earth via the picker).
          </p>
        </div>
      )}

      {/* Accepted template tile (stand-alone) — shown when there's a
          template but no screenshot, so the linkage is still visible. */}
      {!hasScreenshot && acceptedTemplate && (
        <div data-testid="accepted-roof-template-standalone" className="mt-3 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
          <img
            src={acceptedTemplate.thumbnail_url || `/api/roof-templates/${acceptedTemplate._id}/image`}
            alt={acceptedTemplate.name}
            className="w-24 h-18 object-contain bg-white rounded border border-emerald-200 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700">Main Roof Template</p>
            <p className="text-sm font-semibold text-slate-800 truncate" title={acceptedTemplate.name}>{acceptedTemplate.name}</p>
            <div className="flex items-center gap-1.5 mt-1 text-[10px]">
              {acceptedTemplate.hip_count > 0    && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">{acceptedTemplate.hip_count} Hip</span>}
              {acceptedTemplate.ridge_count > 0  && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded font-semibold">{acceptedTemplate.ridge_count} Ridge</span>}
              {acceptedTemplate.valley_count > 0 && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded font-semibold">{acceptedTemplate.valley_count} Valley</span>}
              <button
                onClick={async () => {
                  try { await api.patch(`/properties/${propertyId}`, { roof_template_id: '' }); await load(); } catch { /* ignore */ }
                }}
                className="ml-auto text-[10px] text-rose-600 hover:text-rose-700 font-semibold"
              >Clear</button>
            </div>
          </div>
        </div>
      )}
      {open && (
        <RoofDiagramPicker
          propertyId={propertyId}
          selectedId={prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id || ''}
          address={address}
          onSelect={persist}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
